import { useEffect, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Same URL-normalisation logic as SafeImage — we cannot re-use SafeImage here
// because SafeImage forces `absolute inset-0` positioning that requires the
// parent to have an explicit height.  Clinical photos need to render at their
// natural aspect ratio, so we use a plain <img>.
const proxied = (src) => {
  if (!src) return src;
  if (src.startsWith("data:")) return src;
  if (src.startsWith("/api/")) return `${BACKEND_URL}${src}`;
  if (src.startsWith("/")) return src;
  try {
    const u = new URL(src);
    if (
      u.host === "images.unsplash.com" ||
      u.host === "customer-assets.emergentagent.com" ||
      u.host === "shantanunetralaya.com"
    ) {
      return `${BACKEND_URL}/api/img?url=${encodeURIComponent(src)}`;
    }
  } catch {
    // fall through — return src as-is.
  }
  return src;
};

/**
 * ImageLightbox — full-screen viewer used on clinical case pages so users
 * can inspect slit-lamp / fundus / OCT images at full resolution.  Works on
 * desktop click, mobile tap and keyboard (Escape closes).
 */
export const ImageLightbox = ({ src, alt = "", caption = "", onClose }) => {
  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [handleKey]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/92 flex items-center justify-center p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={alt || "Enlarged clinical image"}
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-3 right-3 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition-colors"
        data-testid="image-lightbox-close"
      >
        <X size={20} />
      </button>
      <div className="relative max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={proxied(src)}
          alt={alt}
          decoding="async"
          className="block max-w-[95vw] max-h-[85vh] w-auto h-auto object-contain rounded-md shadow-2xl bg-black"
        />
        {(caption || alt) && (
          <div className="mt-3 text-white/85 text-xs sm:text-sm text-center max-w-2xl mx-auto leading-relaxed">
            {caption || alt}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

/**
 * ZoomableImage — renders a native <img> at its true aspect ratio (unlike
 * SafeImage which crops to fill its parent) and opens the lightbox on click
 * or tap.
 */
export const ZoomableImage = ({
  src,
  alt = "",
  caption = "",
  className = "",
  imgClassName = "block w-full h-auto",
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!src) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`group relative block w-full text-left cursor-zoom-in bg-black/5 ${className}`}
        aria-label={alt ? `Enlarge: ${alt}` : "Enlarge image"}
        data-testid={testId || "zoomable-image"}
      >
        {!failed ? (
          <img
            src={proxied(src)}
            alt={alt}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
            className={imgClassName}
          />
        ) : (
          <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">
            Image failed to load.
          </div>
        )}
        <span className="pointer-events-none absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/55 text-white text-[10px] uppercase tracking-[0.16em] opacity-70 group-hover:opacity-100 transition-opacity">
          <ZoomIn size={11} /> Zoom
        </span>
      </button>
      {open && <ImageLightbox src={src} alt={alt} caption={caption} onClose={() => setOpen(false)} />}
    </>
  );
};

export default ZoomableImage;
